import type {
  AnyEncounterWorkflowStatus,
  EncounterWorkflowStatusDisplay,
} from '@joyful/utils'
import {
  EncounterWorkflowDisplayStateSchema,
  formatWorkflowStatusDisplayTitle,
  resolveEncounterWorkflowStatusTagDisplay,
} from '@joyful/utils'
import type { ComponentProps, ReactNode } from 'react'

import { Stack } from '../Stack'
import type { StatusPillProps } from './StatusPill'
import { StatusPill, StatusPillLead, StatusPillTail } from './StatusPill'

export interface EncounterWorkflowStatusTagProps extends Omit<
  ComponentProps<'span'>,
  'children' | 'className'
> {
  endAdornment?: ReactNode
  overflowBehavior?: Extract<StatusPillProps['overflowBehavior'], 'none'>
  size?: StatusPillProps['size']
  workflowStatus: AnyEncounterWorkflowStatus
  workflowStatusDisplay?: EncounterWorkflowStatusDisplay | null
}

export function EncounterWorkflowStatusTag({
  endAdornment,
  overflowBehavior,
  size,
  workflowStatus,
  workflowStatusDisplay,
  ...props
}: EncounterWorkflowStatusTagProps) {
  const display = resolveEncounterWorkflowStatusTagDisplay(
    workflowStatus,
    workflowStatusDisplay,
  )
  const status = EncounterWorkflowDisplayStateSchema.meta(display.state)
  const label = formatWorkflowStatusDisplayTitle(display)
  const ariaLabel = props['aria-hidden']
    ? undefined
    : (props['aria-label'] ?? label)
  const segments = (
    <>
      <StatusPillLead variant={status.variant}>{status.title}</StatusPillLead>
      {display.reason ? (
        <StatusPillTail variant={status.variant}>
          {display.reason}
        </StatusPillTail>
      ) : null}
    </>
  )

  if (!endAdornment) {
    return (
      <StatusPill
        aria-label={ariaLabel}
        overflowBehavior={overflowBehavior}
        size={size}
        {...props}
      >
        {segments}
      </StatusPill>
    )
  }

  return (
    <Stack align="center" asChild direction="horizontal" gap="xs">
      <span aria-label={ariaLabel} {...props}>
        <StatusPill overflowBehavior={overflowBehavior} size={size}>
          {segments}
        </StatusPill>
        {endAdornment}
      </span>
    </Stack>
  )
}
